import { Icon } from "@iconify/react";
import { TextInput, Button, PasswordInput, Text } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { Link, useFetcher, useSearchParams } from "@remix-run/react";
import { useAtom } from "jotai";
import { useEffect, useMemo } from "react";

import { GeneralErrorBoundary } from "~/components/error-boundary";
import Logo from "~/components/layout/logo";
import { requireAuth } from "~/lib/auth";
import { modalAtom } from "~/stores";

import { actionHandler } from "./action";
import { LoginSchema, schema } from "./constants";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const sessionToken = await requireAuth(request);
  if (sessionToken) {
    return redirect("/dashboard");
  }

  return json({});
};

export async function action({ request }: ActionFunctionArgs) {
  return actionHandler(request);
}

export const meta: MetaFunction = () => [{ title: "Login" }];

export default function LoginPage() {
  const form = useForm<LoginSchema>({
    initialValues: {
      email: "",
      password: "",
    },
    validate: zodResolver(schema),
  });

  const fetcher = useFetcher<typeof action>();

  function signin(values: typeof form.values) {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      fetcher.submit(
        { ...values },
        {
          method: "post",
          encType: "application/json",
          action: "/auth/v2/login",
        },
      );
    }
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const isModalOpen = useMemo(
    () => searchParams.get("modal") === "error",
    [searchParams],
  );
  const defaultMessage =
    "Masukkan email, kata sandi, dan cabang untuk masuk aplikasi.";
  const modalMessage = useMemo(
    () => searchParams.get("message"),
    [searchParams],
  );

  const [modal, setModal] = useAtom(modalAtom);
  useEffect(() => {
    if (isModalOpen) {
      setModal({
        ...modal,
        isOpen: true,
        illustration: <img src="/error.svg" alt="request otp error" />,
        title: "Gagal Masuk",
        description: modalMessage || defaultMessage,
        confirmText: "Coba Lagi",
        variant: "error",
        onConfirm: () => {
          searchParams.delete("modal");
          searchParams.delete("message");
          setSearchParams(searchParams);
        },
      });
    }
  }, [isModalOpen]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-between px-4">
      <div className="flex flex-col justify-center items-center gap-10 w-5/6 lg:w-fit flex-grow">
        <div className="flex flex-col text-center w-full lg:w-full gap-10">
          <Logo className="w-[180px]" hideIhc={true} />
          <div className="hidden lg:block">
            <div className="font-bold text-2xl text-left">
              Masuk ke akun anda
            </div>
            <div className="font-normal text-lg text-left">
              Masukkan username & kata sandi untuk masuk
            </div>
          </div>
        </div>
        <div className="flex flex-col w-full">
          <div className="w-full">
            <form
              onSubmit={form.onSubmit(signin)}
              className="flex flex-col gap-5"
            >
              <TextInput
                styles={{
                  label: {
                    fontWeight: 600,
                  },
                  root: {
                    width: "100%",
                  },
                }}
                label="Username"
                placeholder="Masukkan username"
                autoComplete="off"
                aria-label="email"
                withAsterisk
                data-cy="email"
                {...form.getInputProps("email")}
                leftSection={<Icon icon="tabler:user" className="h-4 w-4" />}
              />

              <PasswordInput
                styles={{
                  label: {
                    fontWeight: 600,
                  },
                  root: {
                    width: "100%",
                  },
                }}
                label="Kata Sandi"
                withAsterisk
                placeholder="Masukkan kata sandi"
                aria-label="password"
                data-cy="password"
                {...form.getInputProps("password")}
                leftSection={<Icon icon="tabler:lock" className="h-4 w-4" />}
              />

              <Button
                type="submit"
                size="sm"
                color="ihcBlue"
                fullWidth
                className="mt-3"
                data-cy="submit"
                loading={fetcher.state === "submitting"}
              >
                Masuk
              </Button>
              <Link to="/auth/forgot-password" data-cy="forgot-password">
                <Text c="ihcBlue" fw={600} className="text-center">
                  Lupa Kata Sandi?
                </Text>
              </Link>
            </form>
          </div>
        </div>
      </div>

      <div className="flex pb-3">
        <p className="text-sm text-[#5C5F66]">
          Copyright &copy; 2025 Cirql | &nbsp;
        </p>
        <Link to={"/docs/tnc"} className="text-sm text-[#5C5F66]">
          Syarat & Ketentuan
        </Link>
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
